/**
 * The API client (§7.1, §7.4).
 *
 * Responses are parsed with the contract schemas, so a payload that drifts fails here
 * with a readable message rather than three components down as `undefined`. Errors keep
 * their machine code, because the UI branches on `code` and shows `message` unchanged.
 */

import {
  apiErrorSchema,
  categoryScopeChangeResultSchema,
  contextChangeResultSchema,
  createdProjectSchema,
  currentChecklistSchema,
  gitlabGroupSchema,
  inheritancePreviewSchema,
  gitlabProjectSchema,
  meSchema,
  portfolioSchema,
  auditTypeSchema,
  auditWorkspaceSchema,
  createdAuditSchema,
  evidenceCatalogSchema,
  matrixSchema,
  projectOverviewSchema,
  reviewSchema,
  approvedAuditSchema,
  cancelledAuditSchema,
  userSummarySchema,
  kpiDashboardSchema,
  waiversSchema,
  waiverSchema,
  kpiReadingSchema,
  kpiTargetsSchema,
} from '@portal/contracts';
import type {
  ApiError,
  ApiErrorCode,
  AuditType,
  AuditWorkspace,
  CreateAuditInput,
  CancelledAudit,
  CreatedAudit,
  EvidenceCatalog,
  EvidenceSystem,
  Matrix,
  Review,
  CategoryScopeChangeResult,
  CategoryScopeDto,
  ContextChangeResult,
  ContextProfileDto,
  CreateProjectInput,
  CreatedProject,
  KpiDashboard,
  KpiReadingDto,
  KpiTargets,
  UpsertKpiReading,
  ApprovedAudit,
  CurrentChecklist,
  GitlabGroup,
  GitlabProject,
  Me,
  Portfolio,
  ProjectOverview,
  UserSummary,
  PatchProjectInput,
  AssignMemberInput,
  PatchMemberInput,
  Waiver,
  Waivers,
  CreateWaiverInput,
  TeamRole_,
} from '@portal/contracts';
import { z } from 'zod';
import type { ZodType } from 'zod';

import { configValue } from './runtime-config.js';

const BASE_URL =
  configValue('VITE_API_BASE_URL', import.meta.env['VITE_API_BASE_URL']) ?? '/api/v1';

export class ApiClientError extends Error {
  readonly code: ApiErrorCode | 'NETWORK' | 'MALFORMED';
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(
    code: ApiErrorCode | 'NETWORK' | 'MALFORMED',
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Set by the auth layer once a token is held in memory — never in localStorage (§8.1). */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

async function request<T>(path: string, schema: ZodType<T>, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
        ...init.headers,
      },
    });
  } catch (cause) {
    throw new ApiClientError(
      'NETWORK',
      'Could not reach the server. Check your connection and try again.',
      0,
      { cause: String(cause) },
    );
  }

  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    if (parsed.success) {
      const error: ApiError = parsed.data;
      throw new ApiClientError(error.code, error.message, response.status, error.details);
    }
    throw new ApiClientError(
      'MALFORMED',
      `The server returned an unexpected error (${response.status}).`,
      response.status,
    );
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiClientError(
      'MALFORMED',
      `The response from ${path} did not match the contract. ${parsed.error.issues[0]?.message ?? ''}`,
      response.status,
      { issues: parsed.error.issues },
    );
  }

  return parsed.data;
}

export type ReportAudience = 'internal' | 'customer';

/**
 * The Markdown report of a closed audit. Fetched with the bearer token rather than linked
 * to, because the token lives in memory (§8.1) and a plain `<a href>` would not send it.
 */
async function downloadAuditReport(
  id: string,
  audience: ReportAudience,
): Promise<{ filename: string; blob: Blob }> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/audits/${encodeURIComponent(id)}/report.md?audience=${audience}`, {
      headers: accessToken === null ? {} : { authorization: `Bearer ${accessToken}` },
    });
  } catch (cause) {
    throw new ApiClientError(
      'NETWORK',
      'Could not reach the server. Check your connection and try again.',
      0,
      { cause: String(cause) },
    );
  }

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) {
      throw new ApiClientError(parsed.data.code, parsed.data.message, response.status, parsed.data.details);
    }
    throw new ApiClientError(
      'MALFORMED',
      `The server returned an unexpected error (${response.status}).`,
      response.status,
    );
  }

  const named = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '');
  return { filename: named?.[1] ?? `maintenance-report-${audience}.md`, blob: await response.blob() };
}

export const api = {
  downloadAuditReport,
  me: (): Promise<Me> => request('/me', meSchema),
  portfolio: (): Promise<Portfolio> => request('/projects', portfolioSchema),
  users: (): Promise<UserSummary[]> => request('/users', z.array(userSummarySchema)),
  gitlabGroups: (search: string): Promise<GitlabGroup[]> =>
    request(`/gitlab/groups?search=${encodeURIComponent(search)}`, z.array(gitlabGroupSchema)),
  gitlabProjects: (groupId: string): Promise<GitlabProject[]> =>
    request(`/gitlab/groups/${encodeURIComponent(groupId)}/projects`, z.array(gitlabProjectSchema)),
  currentChecklist: (): Promise<CurrentChecklist> =>
    request('/checklist/current', currentChecklistSchema),
  auditTypes: (): Promise<AuditType[]> => request('/audit-types', z.array(auditTypeSchema)),
  auditWorkspace: (id: string, section: string): Promise<AuditWorkspace> =>
    request(`/audits/${id}?section=${encodeURIComponent(section)}`, auditWorkspaceSchema),
  auditReview: (id: string): Promise<Review> => request(`/audits/${id}/review`, reviewSchema),
  /** Moves the audit to IN_REVIEW. The server re-runs the blocking rules; it does not
   *  trust the ones the screen showed. */
  submitAudit: (id: string): Promise<Review> =>
    request(`/audits/${id}/submit`, reviewSchema, { method: 'POST' }),
  /** Freezes the score. Refused for the person who submitted it. */
  approveAudit: (id: string): Promise<ApprovedAudit> =>
    request(`/audits/${id}/approve`, approvedAuditSchema, { method: 'POST' }),
  /** Abandons an unfinished audit. It stays on record as cancelled and is read-only. */
  cancelAudit: (id: string, reason: string | null): Promise<CancelledAudit> =>
    request(`/audits/${id}/cancel`, cancelledAuditSchema, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  auditMatrix: (id: string): Promise<Matrix> => request(`/audits/${id}/matrix`, matrixSchema),
  auditEvidence: (id: string): Promise<EvidenceCatalog> =>
    request(`/audits/${id}/evidence`, evidenceCatalogSchema),
  previewAudit: (input: { projectId: string; auditTypeId: string; periodLabel: string; dueDate: string | null; inheritFromAuditId: string | null }) =>
    request('/audits/preview', inheritancePreviewSchema, { method: 'POST', body: JSON.stringify(input) }),
  createAudit: (input: CreateAuditInput): Promise<CreatedAudit> =>
    request('/audits', createdAuditSchema, { method: 'POST', body: JSON.stringify(input) }),
  patchItem: (
    auditId: string,
    ref: string,
    input: { version: number; state?: string; gapNote?: string; attestation?: string; naJustification?: string },
  ) =>
    request(
      `/audits/${auditId}/items/${ref}`,
      z.object({ ref: z.string(), state: z.string(), version: z.number(), rollUp: z.string().nullable() }),
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
  patchRepoItem: (auditId: string, ref: string, repositoryId: string, input: { version: number; state: string; gapNote?: string }) =>
    request(
      `/audits/${auditId}/items/${ref}/repos/${repositoryId}`,
      z.object({ ref: z.string(), repositoryId: z.string(), state: z.string(), rollUp: z.string().nullable() }),
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
  addEvidence: (auditId: string, ref: string, input: { kind: string; system: EvidenceSystem; url: string; label: string }) =>
    request(
      `/audits/${auditId}/items/${ref}/evidence`,
      z.object({ id: z.string(), evidenceCount: z.number() }),
      { method: 'POST', body: JSON.stringify(input) },
    ),
  createProject: (input: CreateProjectInput): Promise<CreatedProject> =>
    request('/projects', createdProjectSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  project: (id: string): Promise<ProjectOverview> =>
    request(`/projects/${id}`, projectOverviewSchema),
  /** The applicability diff before anything is written (§7.2). */
  previewContext: (id: string, context: ContextProfileDto): Promise<ContextChangeResult> =>
    request(`/projects/${id}/context?dryRun=true`, contextChangeResultSchema, {
      method: 'PUT',
      body: JSON.stringify({ context }),
    }),
  commitContext: (
    id: string,
    context: ContextProfileDto,
    justification: string,
  ): Promise<ContextChangeResult> =>
    request(`/projects/${id}/context`, contextChangeResultSchema, {
      method: 'PUT',
      body: JSON.stringify({ context, justification }),
    }),
  /** The category-scope diff before anything is written — the second scope gate (issue #2). */
  previewCategoryScope: (id: string, categories: CategoryScopeDto): Promise<CategoryScopeChangeResult> =>
    request(`/projects/${id}/category-scope?dryRun=true`, categoryScopeChangeResultSchema, {
      method: 'PUT',
      body: JSON.stringify({ categories }),
    }),
  commitCategoryScope: (
    id: string,
    categories: CategoryScopeDto,
    justification: string,
  ): Promise<CategoryScopeChangeResult> =>
    request(`/projects/${id}/category-scope`, categoryScopeChangeResultSchema, {
      method: 'PUT',
      body: JSON.stringify({ categories, justification }),
    }),

  /**
   * Identity only — name, client, criticality and the rest. The context profile has its
   * own endpoint because it has its own append-only record and its own confirmation diff.
   */
  patchProject: (id: string, input: PatchProjectInput): Promise<ProjectOverview> =>
    request(`/projects/${id}`, projectOverviewSchema, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  // ── Team (TeamOwnership.dc.html) ────────────────────────────────────────────
  // All three return the whole overview, so one round-trip both writes and refreshes
  // the screen rather than leaving the caller to reconcile a partial response.
  assignMember: (id: string, input: AssignMemberInput): Promise<ProjectOverview> =>
    request(`/projects/${id}/members`, projectOverviewSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  patchMember: (
    id: string,
    memberId: string,
    input: PatchMemberInput,
  ): Promise<ProjectOverview> =>
    request(`/projects/${id}/members/${memberId}`, projectOverviewSchema, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  removeMember: (id: string, memberId: string): Promise<ProjectOverview> =>
    request(`/projects/${id}/members/${memberId}`, projectOverviewSchema, {
      method: 'DELETE',
    }),
  // ── Waivers (§3.8, Waivers.dc.html) ─────────────────────────────────────────
  // A waiver records that somebody accepted a gap, and who. It never raises the score.
  waivers: (projectId: string): Promise<Waivers> =>
    request(`/projects/${projectId}/waivers`, waiversSchema),
  createWaiver: (projectId: string, input: CreateWaiverInput): Promise<Waiver> =>
    request(`/projects/${projectId}/waivers`, waiverSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** The second signature. Must be a different person in a different role (§3.8). */
  signWaiver: (projectId: string, waiverId: string, role: TeamRole_): Promise<Waiver> =>
    request(`/projects/${projectId}/waivers/${waiverId}/sign`, waiverSchema, {
      method: 'POST',
      body: JSON.stringify({ role }),
    }),
  rejectWaiver: (projectId: string, waiverId: string): Promise<Waiver> =>
    request(`/projects/${projectId}/waivers/${waiverId}/reject`, waiverSchema, {
      method: 'POST',
    }),

  /** Only the holder may call this — the API refuses anyone else, by design. */
  acknowledgeMembership: (id: string, memberId: string): Promise<ProjectOverview> =>
    request(`/projects/${id}/members/${memberId}/acknowledge`, projectOverviewSchema, {
      method: 'POST',
    }),

  // ── KPI readings (ADR 0010) ─────────────────────────────────────────────────
  kpiDashboard: (projectId: string, period?: string): Promise<KpiDashboard> =>
    request(
      period === undefined
        ? `/projects/${projectId}/kpi`
        : `/projects/${projectId}/kpi?period=${encodeURIComponent(period)}`,
      kpiDashboardSchema,
    ),
  kpiTargets: (projectId: string): Promise<KpiTargets> =>
    request(`/projects/${projectId}/kpi/targets`, kpiTargetsSchema),
  /** Record or correct one figure. Omit `target` to inherit the project's default. */
  recordKpi: (projectId: string, input: UpsertKpiReading): Promise<KpiReadingDto> =>
    request(`/projects/${projectId}/kpi`, kpiReadingSchema, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  setKpiTargets: (projectId: string, input: KpiTargets): Promise<KpiTargets> =>
    request(`/projects/${projectId}/kpi/targets`, kpiTargetsSchema, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
};
